import { useIntl, FormattedMessage } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import { useTheme } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';

import ContentView from '~/components/ContentView';
import generalMessages from '~/containers/App/messages';
import { useCreateEvent } from '~/hooks/api/useCreateEvent';
import EventForm from './EventForm';
import messages from './messages';
import { getRouteApi, useNavigate } from '@tanstack/react-router';

const route = getRouteApi('/experiences/$experienceId/add-event');

export const CreateEvent = () => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const navigate = useNavigate();
  const { experienceId } = route.useParams();
  const experienceIdNum = parseInt(experienceId, 10);

  const gotoExperienceDetails = () => {
    navigate({ to: `/experiences/${experienceIdNum}` });
  };

  const createEventMutation = useCreateEvent({
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.createEventSuccess),
      });
      gotoExperienceDetails();
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.createEventFailure),
      });
    },
  });

  const handleSubmit = ({
    eventDate,
    eventTime,
    location,
    description,
  }: {
    eventDate: string | null;
    eventTime: string | null;
    location: string;
    description: string;
  }) => {
    if (!eventDate || !eventTime) return;

    createEventMutation.mutate({
      experienceId: experienceIdNum,
      requestBody: {
        eventDate: `${eventDate}T${eventTime}:00`,
        location,
        description,
      },
    });
  };

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <IconButton
              color="inherit"
              onClick={gotoExperienceDetails}
              size="large"
              aria-label={intl.formatMessage(messages.back)}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" style={{ marginLeft: theme.spacing(2) }}>
              <FormattedMessage {...messages.createEventHeader} />
            </Typography>
          </Toolbar>
        </AppBar>
      }
      footer={
        <Toolbar>
          <Button
            disabled={createEventMutation.isPending}
            color="primary"
            type="submit"
            form="event-form"
          >
            {createEventMutation.isPending ? (
              <FormattedMessage {...generalMessages.loading} />
            ) : (
              <FormattedMessage {...messages.saveEventButton} />
            )}
          </Button>
        </Toolbar>
      }
    >
      <EventForm onSubmit={handleSubmit} />
    </ContentView>
  );
};

export default CreateEvent;
